import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../home/components/Footer';

interface FAQItem {
  question: string;
  answer: string | JSX.Element;
  category: 'service' | 'qualification' | 'safety' | 'booking' | 'payment' | 'other';
}

export default function FAQPage() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const handleBookingClick = () => {
    navigate('/', { replace: false });
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const categories = [
    { id: 'all', label: 'すべて', icon: 'ri-list-check' },
    { id: 'service', label: 'サービスについて', icon: 'ri-service-line' },
    { id: 'qualification', label: '資格・専門性', icon: 'ri-medal-line' },
    { id: 'safety', label: '安全性・リスク', icon: 'ri-shield-check-line' },
    { id: 'booking', label: '予約・利用方法', icon: 'ri-calendar-check-line' },
    { id: 'payment', label: '料金・支払い', icon: 'ri-money-yen-circle-line' },
    { id: 'other', label: 'その他', icon: 'ri-question-line' }
  ];

  const faqs: FAQItem[] = [
    // サービスについて
    {
      category: 'service',
      question: 'このサービスは何ですか？',
      answer: (
        <div className="space-y-3">
          <p>心理学を専攻する大学生が提供する、<strong>ティーンエイジャー向けのオンラインカウンセリングサービス</strong>です。</p>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-900 font-bold mb-2">⚠️ 重要な注意</p>
            <p className="text-sm text-red-800">本サービスは<strong>医療行為ではありません</strong>。医師による診断、治療、処方等は一切行いません。日常的な悩みや心のケアを目的としたサポートサービスです。</p>
          </div>
        </div>
      )
    },
    {
      category: 'service',
      question: 'どんな悩みを相談できますか？',
      answer: (
        <div className="space-y-3">
          <p className="font-medium">以下のような日常的な悩みに対応しています：</p>
          <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
            <li>恋愛の悩み</li>
            <li>体の変化や悩み</li>
            <li>食べすぎ、食べられない、体重に囚われる悩み</li>
            <li>ひとつの考えが頭から離れない、安心するための行動が止まらない悩み</li>
            <li>親との関係</li>
            <li>毎日の小さな不安やモヤモヤ</li>
          </ul>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mt-4">
            <p className="text-yellow-900 font-bold mb-2">対応できない相談</p>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• 精神疾患の診断を受けている方</li>
              <li>• 自傷・自殺の具体的計画がある相談</li>
              <li>• 現在進行形の虐待・犯罪行為</li>
              <li>• 医学的治療が必要な症状の判断</li>
            </ul>
            <p className="text-sm text-yellow-800 mt-2">→ これらの場合は、医療機関や専門機関への受診をお勧めします。</p>
          </div>
        </div>
      )
    },
    {
      category: 'service',
      question: '医療機関との違いは何ですか？',
      answer: (
        <div className="space-y-3">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <h4 className="font-bold text-blue-900 mb-2">医療機関（精神科・心療内科）</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ 医師による診断・治療</li>
                <li>✓ 薬の処方が可能</li>
                <li>✓ 精神疾患の治療</li>
                <li>✓ 保険適用</li>
              </ul>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <h4 className="font-bold text-orange-900 mb-2">本サービス</h4>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>✓ 大学生による傾聴・サポート</li>
                <li>✓ 診断・治療は行わない</li>
                <li>✓ 日常的な悩みのケア</li>
                <li>✓ 自費（2,000円/35分）</li>
              </ul>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-3">本サービスは医療の代替ではなく、<strong>気軽に話せる相談相手</strong>としてご利用ください。</p>
        </div>
      )
    },

    // 資格・専門性について
    {
      category: 'qualification',
      question: 'カウンセラーはどんな人ですか？',
      answer: (
        <div className="space-y-3">
          <p>心理学を専攻する<strong>大学生</strong>です。</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="font-bold text-gray-900 mb-2">保有資格</p>
            <p className="text-sm text-gray-700"><strong className="text-red-600">国家資格や専門資格は保有していません。</strong></p>
            <p className="text-sm text-gray-600 mt-2">精神科医、臨床心理士、公認心理師等の資格を持つ専門家ではありません。</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="font-bold text-blue-900 mb-2">提供できること</p>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• 心理学の知識に基づいた傾聴</li>
              <li>• 同世代に近い視点でのサポート</li>
              <li>• 安心して話せる環境の提供</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">専門的な治療が必要な場合は、医療機関をご案内します。</p>
        </div>
      )
    },
    {
      category: 'qualification',
      question: 'なぜ資格を持っていないのにサービスを提供しているのですか？',
      answer: (
        <div className="space-y-3">
          <p>日本では、<strong>「カウンセリング」という言葉自体は法的に規制されていません</strong>。医療行為や心理療法を行わない限り、資格がなくても傾聴やサポートサービスを提供することは可能です。</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="font-bold text-yellow-900 mb-2">本サービスの位置づけ</p>
            <p className="text-sm text-yellow-800">医療行為や心理療法ではなく、<strong>「話を聴く」「共感する」「一緒に考える」</strong>といった、友人や先輩に相談するような<strong>ピアサポート</strong>に近いサービスです。</p>
          </div>
          <p className="text-sm text-gray-600">ただし、専門的な治療が必要な場合は対応できないため、医療機関への受診をお勧めしています。</p>
        </div>
      )
    },
    {
      category: 'qualification',
      question: '大学生に相談して大丈夫ですか？',
      answer: (
        <div className="space-y-3">
          <p>本サービスは<strong>医療行為ではない</strong>ため、専門的な治療が必要な方には適していません。</p>
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="font-bold text-green-900 mb-2">大学生カウンセラーのメリット</p>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• 年齢が近く、共感しやすい</li>
              <li>• 気軽に話しやすい雰囲気</li>
              <li>• 同世代の視点でアドバイス</li>
              <li>• 医療機関より敷居が低い</li>
            </ul>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="font-bold text-red-900 mb-2">こんな方は医療機関へ</p>
            <ul className="text-sm text-red-800 space-y-1">
              <li>• 精神疾患の診断を受けている</li>
              <li>• 自傷・自殺念慮がある</li>
              <li>• 日常生活に支障が出ている</li>
              <li>• 薬物療法が必要な症状</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600 mt-3">ご自身の状態に合わせて、適切なサポートをお選びください。</p>
        </div>
      )
    },

    // 安全性・リスクについて
    {
      category: 'safety',
      question: '秘密は守られますか？',
      answer: (
        <div className="space-y-3">
          <p>セッション内でお話しいただいた内容は、<strong>厳重に守秘</strong>いたします。</p>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="font-bold text-red-900 mb-2">守秘義務の例外</p>
            <p className="text-sm text-red-800 mb-2">以下の場合は、あなたの安全を守るため、守秘義務の例外となります：</p>
            <ul className="text-sm text-red-800 space-y-1">
              <li>• 生命・身体に危険が及ぶ可能性がある場合</li>
              <li>• 虐待や犯罪行為が疑われる場合</li>
              <li>• 法令により開示が義務付けられている場合</li>
            </ul>
            <p className="text-sm text-red-800 mt-2">→ 保護者や関係機関へ連絡させていただくことがあります。</p>
          </div>
        </div>
      )
    },
    {
      category: 'safety',
      question: '緊急時はどうなりますか？',
      answer: (
        <div className="space-y-3">
          <p>生命・安全に危険が高いと判断した場合、以下の対応を取らせていただきます：</p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold text-sm">1</span>
              </div>
              <div>
                <h4 className="font-bold text-orange-900 mb-1">セッションの中断</h4>
                <p className="text-sm text-orange-800">あなたの安全を最優先するため、セッションを一時中断します。</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold text-sm">2</span>
              </div>
              <div>
                <h4 className="font-bold text-orange-900 mb-1">専門機関のご案内</h4>
                <p className="text-sm text-orange-800">医療機関や支援窓口をご案内します。</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-white font-bold text-sm">3</span>
              </div>
              <div>
                <h4 className="font-bold text-orange-900 mb-1">保護者・関係機関への連絡</h4>
                <p className="text-sm text-orange-800">必要に応じて、保護者や関係機関へ連絡させていただきます。</p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mt-4">
            <p className="font-bold text-blue-900 mb-2">緊急時の相談窓口</p>
            <div className="text-sm text-blue-800 space-y-1">
              <p><strong>いのちの電話：</strong>0120-783-556（24時間）</p>
              <p><strong>東京いのちの電話：</strong>03-3264-4343（24時間）</p>
              <p><strong>児童相談所：</strong>189（24時間）</p>
              <p><strong>警察：</strong>110（緊急時）</p>
            </div>
          </div>
        </div>
      )
    },
    {
      category: 'safety',
      question: '効果は保証されますか？',
      answer: (
        <div className="space-y-3">
          <p><strong className="text-red-600">効果の保証はできません。</strong></p>
          <p className="text-sm text-gray-700">本サービスは、あくまで心のケアをサポートするものであり、問題の完全な解決や特定の結果を保証するものではありません。</p>
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">効果の感じ方には個人差があります。セッション内容の実践により生じた結果について、当サービスは責任を負いかねます。</p>
          </div>
          <p className="text-sm text-gray-600 mt-3">ご利用は<strong>ご自身の判断と責任</strong>において行っていただきます。</p>
        </div>
      )
    },
    {
      category: 'safety',
      question: '個人情報は安全ですか？',
      answer: (
        <div className="space-y-3">
          <p>ご提供いただいた個人情報は、<strong>セッションの実施と連絡のみ</strong>に使用します。</p>
          <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-700">
            <li>第三者への提供は一切行いません</li>
            <li>目的外の使用は行いません</li>
            <li>適切に管理し、不要になった時点で削除します</li>
          </ul>
        </div>
      )
    },

    // 予約・利用方法について
    {
      category: 'booking',
      question: '予約方法を教えてください',
      answer: (
        <div className="space-y-3">
          <p>ホームページの予約フォームに必要事項を入力して送信してください。</p>
          <p className="text-sm text-gray-600">予約確定後、Google Meetのリンクをメールでお送りします。</p>
        </div>
      )
    },
    {
      category: 'booking',
      question: '18歳未満ですが利用できますか？',
      answer: (
        <div className="space-y-3">
          <p><strong>はい、利用できます。</strong>ただし、<strong className="text-red-600">保護者の同意が必要</strong>です。</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="font-bold text-yellow-900 mb-2">必要な情報</p>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• 保護者のお名前</li>
              <li>• 続柄（父・母など）</li>
              <li>• 保護者のメールアドレス</li>
              <li>• 保護者の電話番号</li>
            </ul>
            <p className="text-sm text-yellow-800 mt-2">※ 保護者のメールアドレスに確認メールをお送りします。</p>
          </div>
          <p className="text-sm text-gray-600">緊急時や必要と判断した場合、保護者の方へご連絡させていただくことがあります。</p>
        </div>
      )
    },
    {
      category: 'booking',
      question: 'キャンセルはできますか？',
      answer: (
        <div className="space-y-3">
          <p><strong>セッション開始の24時間前まで</strong>であれば、キャンセル・日程変更が可能です。</p>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="font-bold text-red-900 mb-2">注意事項</p>
            <ul className="text-sm text-red-800 space-y-1">
              <li>• 無断キャンセルはご遠慮ください</li>
              <li>• 直前のキャンセルが続く場合、今後のご予約をお断りする場合があります</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      category: 'booking',
      question: 'セッションはどのように行われますか？',
      answer: (
        <div className="space-y-3">
          <p>Google Meetを使用した<strong>オンラインセッション</strong>です。</p>
          <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-700">
            <li>時間：1回40分</li>
            <li>方法：ビデオ通話（カメラOFFも可能）</li>
            <li>必要なもの：安定したインターネット環境、スマホ・PC・タブレット</li>
          </ul>
          <p className="text-sm text-gray-600 mt-3">予約確定後、Google Meetのリンクをメールでお送りします。</p>
        </div>
      )
    },

    // 料金・支払いについて
    {
      category: 'payment',
      question: '料金はいくらですか？',
      answer: (
        <div className="space-y-3">
          <div className="bg-[#FFD93D]/10 border-2 border-[#FFD93D]/30 rounded-xl p-6">
            <p className="text-xl font-bold text-[#FF9B8A] mb-2">1回40分：¥3,000</p>
            <p className="text-sm text-gray-700">
              学生価格でご利用いただけます。お支払いはセッション終了後にお願いいたします。
            </p>
          </div>
          <p className="text-sm text-gray-600">
            ※ キャンセルは24時間前までにご連絡ください
          </p>
        </div>
      )
    },
    {
      category: 'payment',
      question: '支払い方法は？',
      answer: (
        <div className="space-y-3">
          <p>現在、支払い方法の詳細は予約確定時にご案内しています。</p>
          <p className="text-sm text-gray-600">お支払いに関するご質問は、予約時にお気軽にお問い合わせください。</p>
        </div>
      )
    },
    {
      category: 'payment',
      question: '返金はできますか？',
      answer: (
        <div className="space-y-3">
          <p>セッション開始後の返金は、原則として<strong>お受けできません</strong>。</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-sm text-yellow-800">キャンセルをご希望の場合は、セッション開始の24時間前までにご連絡ください。</p>
          </div>
        </div>
      )
    },

    // その他
    {
      category: 'other',
      question: '何回くらい受ければいいですか？',
      answer: (
        <div className="space-y-3">
          <p>個人差がありますが、<strong>依存関係を避けるため</strong>、適切な頻度や間隔を設定させていただきます。</p>
          <p className="text-sm text-gray-700">1回で十分な方もいれば、数回継続される方もいます。あなたのペースに合わせて進めていきましょう。</p>
        </div>
      )
    },
    {
      category: 'other',
      question: '録音・録画はできますか？',
      answer: (
        <div className="space-y-3">
          <p><strong className="text-red-600">双方の同意なく録音・録画することは禁止</strong>しています。</p>
          <p className="text-sm text-gray-700">セッション内容を第三者に開示したり、SNS等で公開することもお控えください。</p>
        </div>
      )
    },
    {
      category: 'other',
      question: 'サービスが中止されることはありますか？',
      answer: (
        <div className="space-y-3">
          <p>やむを得ない事情により、サービスを一時的または恒久的に中止する場合があります。</p>
          <p className="text-sm text-gray-600">サービス内容や料金も、予告なく変更する場合があります。あらかじめご了承ください。</p>
        </div>
      )
    },
    {
      category: 'other',
      question: '他の人に勧めてもいいですか？',
      answer: (
        <div className="space-y-3">
          <p><strong>はい、ぜひご紹介ください！</strong></p>
          <p className="text-sm text-gray-700">ただし、セッション内容の詳細は守秘義務がありますので、お話しにならないようお願いします。</p>
        </div>
      )
    }
  ];

  const filteredFAQs = selectedCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === selectedCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-gray-800" style={{ fontFamily: "'Klee One', cursive" }}>
            こころケア
          </Link>
          
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">ホーム</Link>
            <Link to="/profile" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">自己紹介</Link>
            <Link to="/terms" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">注意事項</Link>
            <Link to="/faq" className="text-gray-700 hover:text-gray-900 transition-colors text-sm">よくある質問</Link>
          </div>

          <button 
            onClick={handleBookingClick}
            className="bg-[#FF9B8A] hover:bg-[#FF8A77] text-white px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
          >
            今すぐ予約
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF5F3] via-white to-[#F0F9FF]"></div>
        
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-block px-4 py-2 bg-[#FF9B8A]/10 rounded-full mb-6">
            <span className="text-[#FF9B8A] text-sm font-medium">FAQ</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
            よくある質問
          </h1>
          
          <p className="text-lg text-gray-600">
            サービスについて、よくいただく質問をまとめました
          </p>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8 px-6 bg-red-50 border-y-2 border-red-200">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-white text-lg w-5 h-5 flex items-center justify-center"></i>
            </div>
            <div>
              <h3 className="font-bold text-red-900 mb-2 text-lg">必ずお読みください</h3>
              <p className="text-sm text-red-800 leading-relaxed">
                本サービスは<strong>医療行為ではありません</strong>。心理学を学ぶ大学生が提供する、日常的な悩みのサポートサービスです。<strong>国家資格や専門資格は保有していません</strong>。精神疾患の診断を受けている方、自傷・自殺念慮がある方は、医療機関への受診をお勧めします。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-6 bg-white sticky top-[72px] z-40 border-b border-gray-200">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 overflow-x-auto pb-2">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer flex-shrink-0 ${
                  selectedCategory === category.id
                    ? 'bg-[#FF9B8A] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <i className={`${category.icon} text-base w-4 h-4 flex items-center justify-center`}></i>
                <span>{category.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-[#FF9B8A]/50 transition-all"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-center justify-between gap-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-8 h-8 bg-[#FF9B8A]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-question-line text-[#FF9B8A] text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <h3 className="font-bold text-gray-900 text-base md:text-lg">{faq.question}</h3>
                  </div>
                  <div className={`w-6 h-6 flex items-center justify-center flex-shrink-0 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}>
                    <i className="ri-arrow-down-s-line text-gray-400 text-2xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                </button>

                {openIndex === index && (
                  <div className="px-6 pb-6">
                    <div className="ml-12 text-gray-700 leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-16">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-line text-gray-400 text-3xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <p className="text-gray-500">該当する質問が見つかりませんでした</p>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-16 px-6 bg-gradient-to-br from-[#FFF5F3] to-[#F0F9FF]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF9B8A] rounded-full mb-6">
            <i className="ri-chat-smile-3-line text-white text-3xl w-8 h-8 flex items-center justify-center"></i>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Klee One', cursive" }}>
            まだ疑問がありますか？
          </h2>
          
          <p className="text-lg text-gray-600 mb-8">
            お気軽に予約フォームからお問い合わせください
          </p>

          <button
            onClick={handleBookingClick}
            className="bg-gradient-to-r from-[#FF9B8A] to-[#FFD93D] hover:from-[#FF8A77] hover:to-[#FFC82D] text-white px-10 py-4 rounded-full text-base font-bold transition-all shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer inline-flex items-center gap-3"
          >
            <i className="ri-calendar-check-line text-xl w-5 h-5 flex items-center justify-center"></i>
            予約する
          </button>

          <p className="text-sm text-gray-500 mt-6">
            または、<Link to="/terms" className="text-[#FF9B8A] hover:text-[#FF8A77] font-medium cursor-pointer">注意事項</Link>をご確認ください
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
